import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const START_PAWN = {
  x: 49.8,
  y: 89,
  scale: 0.92,
  opacity: 1,
};

const WALK_DURATION_MS = 950;
const ENTER_DURATION_MS = 430;
const PAWN_VISUAL_CENTER_X = 50.5;
const PAWN_VISUAL_CENTER_Y = 110.9;
const PAWN_IDLE_VERTICAL_OFFSET = -9.6;
const PAWN_DOOR_VERTICAL_OFFSET = 0;

const BUILDINGS = [
  {
    id: "winchester",
    label: "Winchester Chess Club",
    route: "/winchester",
    hotspot: { x: 7.4, y: 28.2, width: 27.8, height: 26.8 },
    doorHotspot: { x: 24.3, y: 49.2, width: 10.4, height: 13.8 },
    target: { x: 27.5, y: 70.5, scale: 0.64, opacity: 1 },
    enter: { x: 27.5, y: 56.8, scale: 0.16, opacity: 0 },
  },
  {
    id: "colden-common",
    label: "Collden Common Club",
    route: "/colden-common",
    hotspot: { x: 56.5, y: 31.2, width: 28.4, height: 23.5 },
    doorHotspot: { x: 65.8, y: 48.4, width: 10.0, height: 13.6 },
    target: { x: 69.2, y: 70.8, scale: 0.64, opacity: 1 },
    enter: { x: 69.2, y: 57.1, scale: 0.16, opacity: 0 },
  },
];

const MENU_ITEMS = [
  {
    id: "home",
    label: "Home",
    route: "/",
    hotspot: { x: 4.2, y: 4.2, width: 17.5, height: 5.0 },
  },
  {
    id: "winchester",
    label: "Winchester Chess Club",
    route: "/winchester",
    doorId: "winchester",
    hotspot: { x: 21.4, y: 4.2, width: 38.5, height: 5.0 },
  },
  {
    id: "colden-common",
    label: "Collden Common Club",
    route: "/colden-common",
    doorId: "colden-common",
    hotspot: { x: 60.8, y: 4.2, width: 34.2, height: 5.0 },
  },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const createWalkPose = (x, y) => {
  const clampedX = clamp(x, 0, 100);
  const clampedY = clamp(y, 0, 100);
  const depth = clampedY / 100;

  return {
    x: clampedX,
    y: clampedY,
    scale: 0.54 + depth * 0.42,
    opacity: 1,
  };
};

function ClubsEntranceHero() {
  const navigate = useNavigate();
  const [pawnPose, setPawnPose] = useState(START_PAWN);
  const [activeBuildingId, setActiveBuildingId] = useState(null);
  const [hoveredBuildingId, setHoveredBuildingId] = useState(null);
  const timersRef = useRef([]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const startBuildingJourney = (
    building,
    destinationRoute = building.route,
  ) => {
    if (activeBuildingId) {
      return;
    }

    clearTimers();
    setHoveredBuildingId(building.id);
    setActiveBuildingId(building.id);
    setPawnPose(building.target);

    timersRef.current.push(
      setTimeout(() => {
        setPawnPose(building.enter);
      }, WALK_DURATION_MS),
    );

    timersRef.current.push(
      setTimeout(() => {
        navigate(destinationRoute);
      }, WALK_DURATION_MS + ENTER_DURATION_MS),
    );
  };

  const handleBuildingClick = (building) => {
    startBuildingJourney(building);
  };

  const handleMenuClick = (item) => {
    if (activeBuildingId) {
      return;
    }

    clearTimers();
    setHoveredBuildingId(null);

    if (item.route === "/") {
      navigate("/");
      return;
    }

    const linkedBuilding = item.doorId
      ? BUILDINGS.find((building) => building.id === item.doorId)
      : null;

    if (linkedBuilding) {
      startBuildingJourney(linkedBuilding, item.route);
      return;
    }

    navigate(item.route);
  };

  const handleSceneClick = (event) => {
    if (activeBuildingId) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    clearTimers();
    setHoveredBuildingId(null);
    setPawnPose(createWalkPose(x, y));
  };

  const pawnVerticalOffset = activeBuildingId
    ? PAWN_DOOR_VERTICAL_OFFSET
    : PAWN_IDLE_VERTICAL_OFFSET;

  return (
    <section className="ch-clubs-hero" aria-label="Chesshood clubs entrance">
      <style>{`
        .ch-clubs-hero{
          width:100%;
          padding:clamp(16px, 2.5vw, 28px);
          background:#0d397b;
        }

        .ch-clubs-frame{
          width:min(100%, 960px);
          margin:0 auto;
          border-radius:28px;
          overflow:hidden;
          box-shadow:0 28px 60px rgba(0, 0, 0, 0.28);
          border:1px solid rgba(255,255,255,0.12);
          background:#0d397b;
        }

        .ch-clubs-scene{
          position:relative;
          width:100%;
          aspect-ratio:1024 / 1536;
          background:url('/clubs.png') center / cover no-repeat;
          background-color:#0d397b;
          isolation:isolate;
        }

        .ch-clubs-menu-button{
          position:absolute;
          z-index:5;
          border:0;
          padding:0;
          background:transparent;
          cursor:pointer;
          border-radius:12px 12px 18px 18px;
          transition:transform 180ms ease, box-shadow 180ms ease;
        }

        .ch-clubs-menu-button::before{
          content:"";
          position:absolute;
          inset:0;
          border-radius:inherit;
          background:linear-gradient(180deg, rgba(255, 208, 73, 0.08), rgba(255, 208, 73, 0.18));
          opacity:0;
          transition:opacity 180ms ease;
        }

        .ch-clubs-menu-button:hover,
        .ch-clubs-menu-button:focus-visible{
          transform:translateY(1px);
          box-shadow:0 0 0 2px rgba(255, 220, 120, 0.42);
        }

        .ch-clubs-menu-button:hover::before,
        .ch-clubs-menu-button:focus-visible::before{
          opacity:1;
        }

        .ch-clubs-building{
          position:absolute;
          z-index:2;
          border:0;
          padding:0;
          background:transparent;
          cursor:pointer;
          border-radius:40px 40px 18px 18px;
          outline:none;
        }

        .ch-clubs-building:disabled,
        .ch-clubs-menu-button:disabled{
          cursor:default;
          opacity:0.65;
        }

        .ch-clubs-door-highlight{
          position:absolute;
          z-index:3;
          pointer-events:none;
          border-radius:20px 20px 8px 8px;
          border:2px solid rgba(255, 213, 116, 0.78);
          background:linear-gradient(180deg, rgba(255, 213, 116, 0.08), rgba(255, 213, 116, 0.24));
          box-shadow:
            0 0 0 1px rgba(255, 239, 183, 0.18),
            0 12px 22px rgba(0, 0, 0, 0.18);
          opacity:0;
          transform:translateY(2px);
          transition:opacity 180ms ease, transform 180ms ease;
        }

        .ch-clubs-door-highlight.is-active{
          opacity:1;
          transform:translateY(0);
        }

        .ch-clubs-pawn{
          position:absolute;
          z-index:4;
          width:clamp(180px, 23vw, 260px);
          pointer-events:none;
          filter:drop-shadow(0 16px 22px rgba(0, 0, 0, 0.45));
          transition:
            left ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            top ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            transform ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            opacity ${ENTER_DURATION_MS}ms ease;
        }

        @media (max-width: 768px){
          .ch-clubs-hero{
            width:100vw;
            margin:0 calc(50% - 50vw);
            padding:0;
            min-height:100svh;
          }

          .ch-clubs-frame{
            width:100vw;
            max-width:none;
            min-height:100svh;
            margin:0;
            border-radius:0;
            border:0;
            box-shadow:none;
          }

          .ch-clubs-scene{
            width:100vw;
            min-height:100svh;
            height:100svh;
            aspect-ratio:auto;
            background-size:100% 100%;
          }
        }

        @media (orientation: portrait){
          .ch-clubs-hero{
            width:100vw;
            margin:0 calc(50% - 50vw);
            padding:0;
            min-height:100svh;
          }

          .ch-clubs-frame{
            width:100vw;
            max-width:none;
            min-height:100svh;
            margin:0;
            border-radius:0;
            border:0;
            box-shadow:none;
          }

          .ch-clubs-scene{
            width:100vw;
            min-height:100svh;
            height:100svh;
            aspect-ratio:auto;
            background-size:100% 100%;
          }
        }
      `}</style>

      <div className="ch-clubs-frame">
        <div className="ch-clubs-scene" onClick={handleSceneClick}>
          {MENU_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ch-clubs-menu-button"
              onClick={(event) => {
                event.stopPropagation();
                handleMenuClick(item);
              }}
              disabled={Boolean(activeBuildingId)}
              aria-label={`Open ${item.label}`}
              title={item.label}
              style={{
                left: `${item.hotspot.x}%`,
                top: `${item.hotspot.y}%`,
                width: `${item.hotspot.width}%`,
                height: `${item.hotspot.height}%`,
              }}
            />
          ))}

          {BUILDINGS.map((building) => {
            const isActive = building.id === activeBuildingId;
            const isHovered = building.id === hoveredBuildingId;

            return (
              <React.Fragment key={building.id}>
                <button
                  type="button"
                  className="ch-clubs-building"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleBuildingClick(building);
                  }}
                  onMouseEnter={() => setHoveredBuildingId(building.id)}
                  onMouseLeave={() => {
                    if (!isActive) {
                      setHoveredBuildingId((currentId) =>
                        currentId === building.id ? null : currentId,
                      );
                    }
                  }}
                  onFocus={() => setHoveredBuildingId(building.id)}
                  onBlur={() => {
                    if (!isActive) {
                      setHoveredBuildingId((currentId) =>
                        currentId === building.id ? null : currentId,
                      );
                    }
                  }}
                  disabled={Boolean(activeBuildingId)}
                  aria-label={`Enter ${building.label}`}
                  title={building.label}
                  style={{
                    left: `${building.hotspot.x}%`,
                    top: `${building.hotspot.y}%`,
                    width: `${building.hotspot.width}%`,
                    height: `${building.hotspot.height}%`,
                  }}
                />
                <div
                  aria-hidden="true"
                  className={`ch-clubs-door-highlight${
                    isActive || isHovered ? " is-active" : ""
                  }`}
                  style={{
                    left: `${building.doorHotspot.x}%`,
                    top: `${building.doorHotspot.y}%`,
                    width: `${building.doorHotspot.width}%`,
                    height: `${building.doorHotspot.height}%`,
                  }}
                />
              </React.Fragment>
            );
          })}

          <img
            src="/hood.png"
            alt=""
            aria-hidden="true"
            className="ch-clubs-pawn"
            style={{
              left: `${pawnPose.x}%`,
              top: `${clamp(pawnPose.y + pawnVerticalOffset, -5, 100)}%`,
              opacity: pawnPose.opacity,
              transform: `translate(-${PAWN_VISUAL_CENTER_X}%, -${PAWN_VISUAL_CENTER_Y}%) scale(${pawnPose.scale})`,
            }}
          />
        </div>
      </div>
    </section>
  );
}

export default ClubsEntranceHero;
